import { useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { addBlog, editBlog, getSingleBlog } from "../../redux/services/blogSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import DescriptionEditor from "./DescriptionEditor";
import BlogFullPagePreviewModal from "./BlogFullPagePreviewModal";
import { LuSparkles, LuGlobe, LuEye, LuUpload, LuImage, LuTrash2 } from "react-icons/lu";
import { getBlogMarketingUrl } from "../../common/urlUtils";

const CATEGORY_OPTIONS = [
  "UPSC Preparation",
  "Current Affairs",
  "Career Guidance",
  "UPSC Prelims",
  "UPSC Mains",
  "CSAT",
  "Governance & Polity",
  "Economy",
  "Science & Technology",
  "History & Culture",
];

const BlogForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const getSingleBlogData = useSelector((state) => state.blog.singleBlogData);
  const queryParams = new URLSearchParams(location.search);
  const blogId = queryParams.get("blogId");
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState(getSingleBlogData?.description || "");
  const [showFullPreview, setShowFullPreview] = useState(false);

  const initialValues = {
    title: "",
    slug: "",
    subTitle: "",
    category: "UPSC Preparation",
    readTime: "8 min read",
    tags: "",
    authorName: "Achiwon Editorial Team",
    authorRole: "UPSC Expert Faculty",
    metaTitle: "",
    metaDescription: "",
    image: "",
    isFeatured: false,
    description: description,
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    slug: Yup.string().optional(),
    subTitle: Yup.string().required("Sub Title is required"),
    category: Yup.string().required("Category is required"),
    image: Yup.string().required("Image is required"),
    description: Yup.string().required("Description is required"),
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        title: values.title,
        slug: values.slug || values.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, ""),
        subTitle: values.subTitle,
        category: values.category,
        readTime: values.readTime || "8 min read",
        image: values.image,
        description: values.description,
        metaTitle: values.metaTitle || values.title,
        metaDescription: values.metaDescription || values.subTitle,
        author: {
          name: values.authorName || "Achiwon Editorial Team",
          role: values.authorRole || "UPSC Expert Faculty",
          avatar: "https://randomuser.me/api/portraits/women/44.jpg",
        },
        tags: typeof values.tags === "string"
          ? values.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : values.tags || [],
        isFeatured: Boolean(values.isFeatured),
      };

      if (blogId) {
        payload.blogId = blogId;
      }

      let response;
      if (blogId) {
        response = await dispatch(editBlog(payload));
      } else {
        response = await dispatch(addBlog(payload));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message || "Blog saved successfully!", {
          autoClose: 2000,
          pauseOnHover: false,
        });
        setDescription("");
        setLoading(false);
        navigate("/blog");
        resetForm();
      }
    } catch (error) {
      toast.error("An error occurred while saving the blog.");
    } finally {
      setLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const generateSlugFromTitle = () => {
    if (!values.title) return;
    const clean = values.title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "");
    setValues({ ...values, slug: clean });
  };

  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleBlog(blogId));
    } catch (error) {}
  }, [dispatch, blogId]);

  useEffect(() => {
    if (blogId) {
      getSingleRecord();
    }
  }, [blogId, getSingleRecord]);

  useEffect(() => {
    if (blogId && getSingleBlogData) {
      setValues({
        title: getSingleBlogData?.title ?? "",
        slug: getSingleBlogData?.slug ?? "",
        subTitle: getSingleBlogData?.subTitle ?? "",
        category: getSingleBlogData?.category ?? "UPSC Preparation",
        readTime: getSingleBlogData?.readTime ?? "8 min read",
        tags: Array.isArray(getSingleBlogData?.tags)
          ? getSingleBlogData.tags.join(", ")
          : getSingleBlogData?.tags ?? "",
        authorName: getSingleBlogData?.author?.name ?? "Achiwon Editorial Team",
        authorRole: getSingleBlogData?.author?.role ?? "UPSC Expert Faculty",
        metaTitle: getSingleBlogData?.metaTitle ?? "",
        metaDescription: getSingleBlogData?.metaDescription ?? "",
        image: getSingleBlogData?.image ?? "",
        isFeatured: Boolean(getSingleBlogData?.isFeatured),
        description: getSingleBlogData?.description ?? "",
      });
    }
  }, [blogId, getSingleBlogData, setValues]);

  useEffect(() => {
    if (blogId && getSingleBlogData?.description) {
      setDescription(getSingleBlogData.description);
    } else {
      setDescription("");
    }
  }, [getSingleBlogData, blogId]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const imageURL = URL.createObjectURL(file);
    setPreview(imageURL);
    const formData = new FormData();
    formData.append("image", file);
    try {
      setUploadingImage(true);
      const response = await dispatch(fileUpload(formData));
      const uploadedUrl =
        response?.payload?.data ||
        response?.payload?.fileUrl ||
        (typeof response?.payload === "string" ? response.payload : null);
      if (uploadedUrl) {
        setValues((prev) => ({ ...prev, image: uploadedUrl }));
        toast.success("Image uploaded successfully!", { autoClose: 2000 });
      } else {
        toast.error("Could not obtain uploaded image URL.");
      }
    } catch (error) {
      toast.error("Error uploading image file.");
    } finally {
      setUploadingImage(false);
    }
  };

  const marketingUrl = getBlogMarketingUrl(values.category, values.slug || "your-custom-slug");

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-border/60 pb-5">
            <div>
              <p className="md:text-3xl text-xl font-bold uppercase text-primary">
                {action} Blog
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Manage rich content, dynamic tables, MCQ tests, and marketing slugs.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowFullPreview(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#114598] to-[#2563EB] hover:from-[#F68122] hover:to-amber-500 text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer shrink-0"
              title="Preview complete landing page appearance with TOC, tables, and quizzes"
            >
              <LuEye className="text-base" /> 👁️ Full Page Live Preview
            </button>
          </div>

          <form className="mt-8" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-foreground">Blog Title *</label>
                <input
                  type="text"
                  name="title"
                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. Ranks in UPSC: Requirements for IAS, IPS and IFS"
                />
                {touched.title && errors.title && (
                  <div className="text-red-500 text-xs mt-1">{errors.title}</div>
                )}
              </div>

              {/* Marketing URL Slug */}
              <div className="md:col-span-2 bg-[#F8FAFC] border border-border rounded-2xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                  <label className="text-sm font-bold text-[#124699] flex items-center gap-1.5">
                    <LuGlobe className="text-base" /> Marketing URL Slug (Clean SEO Friendly URL)
                  </label>
                  <button
                    type="button"
                    onClick={generateSlugFromTitle}
                    className="inline-flex items-center gap-1 text-xs font-bold text-white bg-primary hover:bg-[#F48224] px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                  >
                    <LuSparkles /> ⚡ Generate from Title
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground font-mono hidden sm:inline">
                    /blog/
                  </span>
                  <input
                    type="text"
                    name="slug"
                    className="w-full px-3 py-2 border border-border rounded-xl bg-white text-xs font-mono text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                    value={values.slug}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="ranks-in-upsc-requirements-for-ias-ips-and-ifs"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                  <span>Live URL Preview:</span>
                  <span className="font-mono text-[#124699] font-semibold">{marketingUrl}</span>
                </p>
              </div>

              {/* Sub Title / Excerpt */}
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-foreground">
                  Sub Title / Excerpt *
                </label>
                <textarea
                  name="subTitle"
                  rows={2}
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.subTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Short introductory summary for cards and search snippets..."
                />
                {touched.subTitle && errors.subTitle && (
                  <div className="text-red-500 text-xs mt-1">{errors.subTitle}</div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-semibold text-foreground">Category *</label>
                <select
                  name="category"
                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.category}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {touched.category && errors.category && (
                  <div className="text-red-500 text-xs mt-1">{errors.category}</div>
                )}
              </div>

              {/* Read Time */}
              <div>
                <label className="text-sm font-semibold text-foreground">Read Time</label>
                <input
                  type="text"
                  name="readTime"
                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.readTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. 8 min read"
                />
              </div>

              {/* Tags */}
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-foreground">
                  Tags / Keywords (comma separated)
                </label>
                <input
                  type="text"
                  name="tags"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.tags}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. UPSC CSE, IAS, IPS, Governance, Cut-off Trends"
                />
                <span className="text-[11px] text-muted-foreground">
                  These tags will appear as badges and pill filters on the blog details page.
                </span>
              </div>

              {/* Featured Blog Toggle Button (On/Off) */}
              <div className="md:col-span-2 bg-[#F0FDF4] border border-[#BBF7D0] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#15803D]">Featured Blog On / Off</span>
                    <span
                      className={`text-[11px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
                        values.isFeatured
                          ? "bg-[#16A34A] text-white shadow-xs"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {values.isFeatured ? "✓ FEATURED ON" : "STANDARD OFF"}
                    </span>
                  </div>
                  <p className="text-xs text-[#166534] mt-1">
                    When turned <strong>ON</strong>, this blog will be highlighted in the top Featured Card on the website. If multiple blogs are ON, they will automatically scroll in a carousel.
                  </p>
                </div>

                {/* On / Off Switch */}
                <button
                  type="button"
                  onClick={() => setValues({ ...values, isFeatured: !values.isFeatured })}
                  className={`relative inline-flex h-8 w-16 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    values.isFeatured ? "bg-[#16A34A]" : "bg-gray-300"
                  }`}
                  role="switch"
                  aria-checked={values.isFeatured}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                      values.isFeatured ? "translate-x-8" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Author Details */}
              <div>
                <label className="text-sm font-semibold text-foreground">Author Name</label>
                <input
                  type="text"
                  name="authorName"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.authorName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Achiwon Editorial Team"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">Author Role</label>
                <input
                  type="text"
                  name="authorRole"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.authorRole}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="UPSC Expert Faculty"
                />
              </div>

              {/* SEO Meta Fields */}
              <div>
                <label className="text-sm font-semibold text-foreground">
                  SEO Meta Title (Google Search)
                </label>
                <input
                  type="text"
                  name="metaTitle"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.metaTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Optional custom title for search engines"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">
                  SEO Meta Description
                </label>
                <input
                  type="text"
                  name="metaDescription"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  value={values.metaDescription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Optional 150-160 character description for search snippets"
                />
              </div>

              {/* Featured Image Upload Card */}
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-foreground mb-1.5 block">
                  Featured Blog Banner Image *
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />

                {/* Upload & Preview Box */}
                <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-[#F8FAFC] p-4 transition-all hover:border-[#124699]">
                  {(preview || values.image) ? (
                    <div className="flex flex-col sm:flex-row items-center gap-5">
                      <div className="relative w-full sm:w-56 h-36 rounded-xl overflow-hidden border border-border shadow-xs bg-white shrink-0 group">
                        <img
                          src={preview || values.image}
                          alt="Featured Blog Preview"
                          className="w-full h-full object-cover"
                        />
                        {uploadingImage && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-bold gap-2">
                            <span className="animate-spin">⏳</span> Uploading...
                          </div>
                        )}
                      </div>

                      <div className="flex-1 w-full space-y-3">
                        <div>
                          <p className="text-xs font-bold text-foreground">Active Banner Image</p>
                          <p className="text-[11px] text-muted-foreground break-all font-mono mt-0.5">
                            {values.image || "Local preview pending upload"}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2.5">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#124699] text-white text-xs font-bold rounded-lg hover:bg-[#0e3575] transition-colors cursor-pointer"
                          >
                            <LuUpload className="text-sm" /> Change Image
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPreview(null);
                              setValues((prev) => ({ ...prev, image: "" }));
                            }}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-red-50 text-red-600 border border-red-200 text-xs font-bold rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                          >
                            <LuTrash2 className="text-sm" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer py-8 flex flex-col items-center justify-center text-center group"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-[#EFF6FF] text-[#124699] flex items-center justify-center text-2xl group-hover:scale-110 group-hover:bg-[#124699] group-hover:text-white transition-all shadow-xs">
                        <LuImage />
                      </div>
                      <p className="mt-3 text-sm font-bold text-foreground group-hover:text-[#124699] transition-colors">
                        Click here to select and upload Blog Image
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supports PNG, JPG, WEBP (Recommended ratio: 16:9 or 1536×1024)
                      </p>
                      <button
                        type="button"
                        className="mt-3.5 inline-flex items-center gap-1.5 px-4 py-2 bg-[#124699] text-white text-xs font-bold rounded-lg group-hover:bg-[#F48224] transition-colors shadow-xs"
                      >
                        <LuUpload className="text-sm" /> Browse File
                      </button>
                    </div>
                  )}

                  {/* Or Manual URL Input */}
                  <div className="mt-3 pt-3 border-t border-gray-200/80 flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-gray-500 shrink-0">Or paste Image URL:</span>
                    <input
                      type="text"
                      name="image"
                      value={values.image}
                      onChange={(e) => {
                        handleChange(e);
                        setPreview(e.target.value);
                      }}
                      placeholder="https://example.com/images/upsc-banner.png"
                      className="w-full text-xs font-mono px-3 py-1.5 border border-border rounded-lg bg-white focus:ring-1 focus:ring-[#124699] focus:outline-none"
                    />
                  </div>
                </div>

                {touched.image && errors.image && (
                  <div className="text-red-500 text-xs mt-1.5">{errors.image}</div>
                )}
              </div>

              {/* Rich Description Editor with Mode Tabs and Quick Insert Buttons */}
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-foreground mb-1 block">
                  Blog Content (Dynamic HTML / Visual / Preview) *
                </label>
                <DescriptionEditor
                  value={description}
                  onChange={(content) => {
                    setDescription(content);
                    setValues({ ...values, description: content });
                  }}
                  readOnly={false}
                />
                {touched.description && errors.description && (
                  <div className="text-red-500 text-xs mt-1">{errors.description}</div>
                )}
              </div>
            </div>

            {/* Submit & Preview Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <button
                type="button"
                onClick={() => setShowFullPreview(true)}
                className="px-6 min-h-[50px] inline-flex justify-center items-center gap-2 text-[#114598] border-2 border-[#114598] bg-white text-sm font-bold rounded-xl hover:bg-blue-50 transition-all shadow-xs cursor-pointer"
              >
                <LuEye className="text-base" /> 👁️ Preview Full Page
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-[280px] min-h-[50px] flex justify-center items-center text-white bg-primary text-base font-bold rounded-xl hover:bg-[#F48224] transition-all shadow-md cursor-pointer disabled:opacity-50"
              >
                {loading ? <div className="loader"></div> : `Save & Publish Blog`}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Full Page Responsive Live Preview Modal */}
      <BlogFullPagePreviewModal
        isOpen={showFullPreview}
        onClose={() => setShowFullPreview(false)}
        blogData={values}
      />
    </div>
  );
};

export default BlogForm;


